import { useMemo } from 'react'
import { IUser } from 'types'
import { Maybe } from 'types/graphqlTypes'
import { IShipper } from 'apps/marketplace/pages/shippers/utils/types/shipper'
import { IShipperRelationshipInfoFragment } from '../graphql/ShipperRelationshipInfoFragment'
import { groupShipperRelationships } from '../helpers/groupShipperRelationships'
import { TShipperTeam } from '../types'

interface IUseShipperRelationshipsParams {
  newShipperTransfixTeamForm: boolean
  shipper: Maybe<IShipper>
  shipperTeam: Maybe<TShipperTeam>
}

const parseUser = (user: Maybe<Partial<IUser>>) => {
  const { firstName = '', lastName = '', parentUserId = '' } = user ?? {}
  return {
    fullName: firstName && lastName ? `${firstName} ${lastName}` : undefined,
    parentUserId,
  }
}

const parseShipperRelationship = (relationship: Maybe<IShipperRelationshipInfoFragment>) => {
  const { user } = relationship ?? {}
  const { fullName, parentUserId } = user ?? {}
  return {
    fullName: fullName ?? undefined,
    parentUserId,
  }
}

export const useShipperRelationships = ({
  newShipperTransfixTeamForm,
  shipper,
  shipperTeam,
}: IUseShipperRelationshipsParams) =>
  useMemo(() => {
    if (newShipperTransfixTeamForm) {
      const {
        accountManager,
        contactReferer,
        financeRep,
        primaryAccountOpsRep,
        primaryCarrierOpsRep,
        salesExecutive,
        secondaryAccountOpsReps,
        secondaryCarrierOpsReps,
      } = groupShipperRelationships(shipperTeam?.shipperRelationships)
      const { opsCustomerEmail, podId } = shipperTeam ?? {}

      return {
        accountManager: parseShipperRelationship(accountManager),
        contactReferer: parseShipperRelationship(contactReferer),
        financeRep: parseShipperRelationship(financeRep),
        salesExecutive: parseShipperRelationship(salesExecutive),
        primaryAccountOpsRep: parseShipperRelationship(primaryAccountOpsRep),
        secondaryAccountOpsReps: secondaryAccountOpsReps?.map(rep => parseShipperRelationship(rep)),
        primaryCarrierOpsRep: parseShipperRelationship(primaryCarrierOpsRep),
        secondaryCarrierOpsReps: secondaryCarrierOpsReps?.map(rep => parseShipperRelationship(rep)),
        opsCustomerEmail,
        podId,
      }
    }

    const { contactReferer, financeRep, opsCustomerEmail, opsRep, podId, saleRep } = shipper ?? {}

    return {
      accountManager: parseUser(opsRep),
      contactReferer: parseUser(contactReferer),
      financeRep: parseUser(financeRep),
      salesExecutive: parseUser(saleRep),
      opsCustomerEmail,
      podId,
    }
  }, [newShipperTransfixTeamForm, shipper, shipperTeam])
